import { BillingPeriod, BillingType, SERVICE_TYPE } from "providers/Auth";
import { MagicLink } from "./address";
import {
  IntegrationCredential,
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types";
import { BillingConfiguration } from "./billing-configuration";

export enum FEATURES {
  FREE_CHECKOUT = "free_checkout",
  SDK = "sdk",
  DELINQUENCY_REPORTING = "delinquency_reporting",
  API_ACCESS = "api_access",
  BOOM_SCREEN = "boom_screen",
  BOOM_CRM = "boom_crm",
  PROVISIONED_PHONE_NUMBER = "provisioned_phone_number",
  CRM_FEATURE_WORKFLOWS = "crm_feature_workflows",
  PRESCREENING_IDV = "prescreening_idv",
  RENT_REPORTING = "rent_reporting",
  ALLOW_MANUAL_PROPERTY_MANAGEMENT = "allow_manual_property_management",
  SITES_AND_COMMUNITIES = "sites_and_communities",
  SHOW_FULL_SSN = "show_full_ssn",
  USE_PROPERTY_NAME_IN_EMAIL = "use_property_name_in_email",
  PROPERTY_GROUP_MARKET = "property_group_market",
  USE_PROPERTY_NAME_IN_SUBMISSION_DISCLOSURE = "use_property_name_in_submission_disclosure",
  ENTERPRISE_PROPERTYWARE = "enterprise_propertyware",
  MANUAL_APPLICATION_PROPERTY_MANAGER_SUBMISSION_DISCLOSURE = "manual_application_property_manager_submission_disclosure",
  AI_APPLICATION_SUMMARY = "ai_application_summary",
  TRANSUNION_CREDIT_REPORT = "transunion_credit_report",
  PMS_LISTING_SYNC = "pms_listing_sync",
  ENABLE_CREDIT_IMPACT_DASHBOARD = "enable_credit_impact_dashboard",
  HOME_SITE_RENT = "home_site_rent",
  WEEKLY_BILLING = "weekly_billing",
  SCHEDULING = "scheduling",
  TEMPLATE_FEE_COLLECTION = "template_fee_collection",
  USE_PROPERTYWARE_MULTIMEDIA_SCRAPER = "use_propertyware_multimedia_scraper",
  WIP_FEAT_PREMIUM_RENTAL_VERIFICATION = "wip_feat_premium_rental_verification",
  COMMUNICATION = "communication",
  COMMUNICATION_SMS = "communication_sms",
  ENABLE_IDENTITY_THEFT_PROTECTION = "enable_identity_theft_protection",
  USE_SLIM_APPLICATIONS_API = "use_slim_applications_api",
  EXTENDED_BANK_STATEMENTS = "extended_bank_statements",
  STRIPE_MANAGED_BILLING = "stripe_managed_billing",
  MULTIPLE_PROPERTY_MANAGEMENT_SYSTEMS = "multiple_property_management_systems",
  COMBINED_SEARCH_V2 = "combined_search_v2",
  ENGLISH_ONLY = "english_only",
  WIP_BOOM_FINANCE = "wip_boom_finance",
  CRM_AI_VOICE_AGENT = "crm_ai_voice_agent"
}

type BankAccount = {
  id: number;
  account_number: string;
  routing_number: string;
  account_nickname: string;
  account_subtype: "checking" | "savings";
  default: boolean;
  name: string;
  last4?: string;
};

export type Partner = {
  name: string;
  url?: string;
  logo_url: string | null;
  algolia: {
    app_id?: string;
    client_search_key?: string;
    index_name?: string;
  } | null;
  billing_period: BillingPeriod;
  pending_billing_period?: BillingPeriod;
  pending_billing_period_effective_at?: string;
  billing_type?: BillingType;
  features: FEATURES[];
  service_type?: SERVICE_TYPE;
  property_management_system?: {
    name: PropertyManagementSystem | "";
    last_synced_at: string | null;
  };
  integration_credentials?: IntegrationCredential[];
  magic_link?: MagicLink;
  invoice_billing_day: number;
  payout_billing_day: number;
  payout_bank_account: BankAccount;
  invoice_bank_account: BankAccount;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
    rental_history_verification_completed: boolean;
  };
  partner_features: PartnerFeatures[];
  can_update_billing_type?: boolean;
  listing_url: string;
  auto_enrollment_default?: string | null;
  auto_enrollment_enabled_date?: string | null;
  enrollment_eligibility_date?: string | null;
  hubspot_hub_id?: string;
  codebox_connected?: boolean;
  sentrilock_connected?: boolean;
  zillow_connected?: boolean;
  zumper_connected?: boolean;
  colors?: Record<string, Record<string, string>>;
  apartments_com_connected?: boolean;
  boom_finance_external_id?: string | null;
  appfolio_supergood_enabled?: boolean;
  billing_configuration: BillingConfiguration | null;
  property_type?:
    | "single_family"
    | "multi_family"
    | "manufactured_housing"
    | null;
};
